import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";

export const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (q.trim().length < 2) { setResults([]); return; }
      api.get(`/search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => setResults(data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  if (!open) return null;

  const go = (slug) => {
    onClose();
    setQ("");
    setResults([]);
    navigate(`/post/${slug}`);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[rgba(7,7,9,0.97)] backdrop-blur-xl overflow-y-auto" data-testid="search-overlay">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <div className="flex items-center gap-4 border-b border-[#232530] pb-4">
          <Search size={22} className="text-[#581313] shrink-0" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search the vault — titles, tags, phrases…"
            className="flex-1 bg-transparent font-serif text-2xl text-[#ede8e1] placeholder:text-[#57534e] focus:outline-none"
            data-testid="search-input" />
          <button onClick={onClose} className="text-[#78716c] hover:text-[#ede8e1] transition-colors" data-testid="search-close-button">
            <X size={22} />
          </button>
        </div>
        <div className="mt-8 space-y-1" data-testid="search-results">
          {results.map((p) => (
            <button key={p.id} onClick={() => go(p.slug)} data-testid={`search-result-${p.slug}`}
              className="w-full text-left group flex items-baseline gap-6 py-4 border-b border-[#14151b] hover:bg-[#0d0e12] transition-colors px-2">
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#581313] shrink-0 w-24">{p.section}</span>
              <span className="flex-1">
                <span className="block font-serif text-xl text-[#ede8e1] group-hover:text-[#ede8e1]">{p.title}</span>
                {p.excerpt && <span className="block mt-1 text-sm font-light text-[#a8a29e] line-clamp-1">{p.excerpt}</span>}
              </span>
            </button>
          ))}
          {q.trim().length >= 2 && results.length === 0 && (
            <p className="py-10 text-center font-mono text-xs tracking-[0.3em] uppercase text-[#57534e]" data-testid="search-empty">
              Nothing in the vault answers to that name.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
